import React from "react";
import { ILookupDictionary } from "../../../lookup/types/ILookupDictionary";
import { getOpenDistributionsThunk, triggerManualJob } from "../../../redux/thunks/HomeThunks";
import { ITriggerManualJob } from "../../../redux/types/ITriggerManualJob";
import { INVOICE_EXPORT_FORMAT_LOOKUP } from "../../../lookup/Consts";
import { getLookupValues } from "../../../lookup/services/LookupHelpers";
import { IDistributionState } from "../../../redux/types/IDistributionState";
import ActionButton from "../../../repertoire/components/actionButton/ActionButton";
import TextDataInput from "../../../repertoire/components/textDataInput/TextDataInput";
import DropdownDataInput from "../../../repertoire/components/dropdownDataInput/DropdownDataInput";
import { CREATE_MANUAL_JOB_TITLE, DISTRIBUTION_KEY, DISTRIBUTION_TITLE, FORMAT_KEY, FORMAT_TITLE, INVOICE_EXPORT_JOB_TYPE, INVOICE_NUMBER_KEY, INVOICE_NUMBER_TITLE, POOL_KEY, POOL_TITLE } from "../../Consts";
import { getPoolsThunk } from "../../../redux/thunks/DataIngestionThunks";
import { IUsagePoolState } from "../../../redux/types/IUsagePoolState";

export interface InvoiceExportFormatParametersProps {
    triggerManualJob: typeof triggerManualJob;
    createNewJob: any;
    setManualJobParameters: (parameters: ITriggerManualJob) => void;
    lookupValues: ILookupDictionary;
    getOpenDistributions: typeof getOpenDistributionsThunk;
    openDistributions?: IDistributionState[];
	getPools: typeof getPoolsThunk;
	pools?: IUsagePoolState[];
    scheduledJobSet?: (value: boolean) => void;
    onError?: (error: any) => void;
}

interface InvoiceExportFormatParametersState {
    format: string;
    distributionCode: string;
    poolCode: string;
    poolOptions: {
        code: string;
        description: string;
        extraFields?: object;
    }[];
    invoiceNumber: string;
    generalError: string;
    loading: boolean;
}

export class InvoiceExportFormatParameters extends React.Component<
    InvoiceExportFormatParametersProps,
    InvoiceExportFormatParametersState
> {
    constructor(props: InvoiceExportFormatParametersProps) {
        super(props);

        this.state = {
            format: null,
            distributionCode: null,
            poolCode: null,
            poolOptions: [],
            invoiceNumber: "",
            generalError: "",
            loading: false
        };
    }

    componentDidMount() {
        const { getOpenDistributions, openDistributions, getPools, pools } = this.props;

        if (openDistributions?.length === 0) getOpenDistributions();
		if (pools?.length === 0) getPools();
    }

    getFormatOptions() {
		const { lookupValues } = this.props;
		let formatOptions = getLookupValues(
			INVOICE_EXPORT_FORMAT_LOOKUP,
            lookupValues
        ).map((e) => {
			return {
				code: e.code,
                description: e.description
            };
        });
		
        return formatOptions;
    }

    handleChangeFormat(value) {
        this.setState({ format: value.toString() });
    }

    getDistributionCodeOptions() {
        let distributionCodes = this.props.openDistributions.map((e) => {
            return {
                code: e.distributionCode,
                description: `${e.distributionCode} - ${e.description}`
            };
        });

        return distributionCodes;
    }

    handleChangeDistributionCode(value) {
        this.setState({ distributionCode: value.toString() });

        if (value) {
            let distributionId = this.props.openDistributions.filter((e) => e.distributionCode === value)[0].distributionID;
            this.loadPools(distributionId);
        }
    }

    handleChangeInvoiceNumber(value) {
        this.setState({ invoiceNumber: value.toString() });
    }

    handleChangePoolCode(value) {
        this.setState({ poolCode: value.toString() });
    }

    loadPools = (distributionId: number) => {
        const { openDistributions, pools } = this.props;
		const filteredPools = pools.filter(p => 
			openDistributions?.find(d => d.distributionID === distributionId)
				?.distributionPools?.some(dp => dp.poolID === p.poolID)
		) || [];
		
		const poolOptionsByDistributionId = filteredPools.map((pool) => ({
            code: pool.poolID.toString(),
            description: pool.poolCode
        }));

		this.setState({ poolOptions: poolOptionsByDistributionId });
    }

    onClick = () => {
        const { createNewJob,scheduledJobSet, onError } = this.props;
        try {
            this.setState({ loading: true });
            if (this.setParamsandTriggerJob()) {
                createNewJob();
                scheduledJobSet(true);
            }
        } catch (err: any) {
            onError?.(err?.toString() || "An error occurred");
        } finally {
            this.setState({ loading: false });
        }
    };

    setParamsandTriggerJob = () => {
        const { setManualJobParameters, triggerManualJob } = this.props;
        const { format, distributionCode, invoiceNumber } = this.state;

        if (this.validateFields()) {
            const manualJobParameters: ITriggerManualJob = {
                jobType: INVOICE_EXPORT_JOB_TYPE,
                jobParameters: JSON.stringify({
                    format,
                    distributionCode,
                    poolId: this.state.poolCode,
                    invoiceNumber: invoiceNumber ? parseInt(invoiceNumber, 10) : null,
                })
            };

            setManualJobParameters(manualJobParameters);
            this.setState({ generalError: "" });
            return triggerManualJob(manualJobParameters);
        }
        else {
            this.setState({ generalError: "Please fill in all the required fields" });
            return false;
        }
    };

    validateFields = () => {
        const { format, distributionCode, invoiceNumber } = this.state;
        return !format || !distributionCode || !invoiceNumber ? false : true;
    };

    dismissAlert = () => {
        this.setState({ generalError: "" });
    };

    render() {
        const { generalError } = this.state;

        return (
            <>
                {generalError && (
                    <div className="alert alert-danger alert-dismissible">
                        <span>{generalError}</span>
                        <button type="button" className="close" aria-label="Close" onClick={this.dismissAlert}>
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                )}
                <div className="form-group col-md-6 col-sm-6 col-xs-12">
                    <DropdownDataInput
                        label={FORMAT_TITLE}
                        fieldName={FORMAT_KEY}
                        changeData={this.handleChangeFormat.bind(this)}
                        value={this.state.format}
                        options={this.getFormatOptions()}
                        allowNull={false}
                        isHidden={false}
                        isMandatory
                    />
                </div>
                <div className="form-group col-md-6 col-sm-6 col-xs-12">
                    <DropdownDataInput
                        label={DISTRIBUTION_TITLE}
                        fieldName={DISTRIBUTION_KEY}
                        changeData={this.handleChangeDistributionCode.bind(this)}
                        value={this.state.distributionCode}
                        options={this.getDistributionCodeOptions()}
                        allowNull={false}
                        isHidden={false}
                        isMandatory
                    />
                </div>
                <div className="form-group col-md-6 col-sm-6 col-xs-12">
                    <TextDataInput
                        label={INVOICE_NUMBER_TITLE}
                        value={this.state.invoiceNumber}
                        changeData={this.handleChangeInvoiceNumber.bind(this)}
                        fieldName={INVOICE_NUMBER_KEY}
                        fieldType="integer"
                        isHidden={false}
                        isMandatory
                    />
                </div>
                <div className="form-group col-md-6 col-sm-6 col-xs-12">
                    <DropdownDataInput
                        label={POOL_TITLE}
                        fieldName={POOL_KEY}
                        changeData={this.handleChangePoolCode.bind(this)}
                        value={this.state.poolCode}
                        options={this.state.poolOptions}
                        allowNull={false}
                        isHidden={false}
                        isMandatory
                        readOnly={this.state.poolOptions.length === 0}
                    />
                </div>
                <div className="form-group col-lg-4 col-md-4 col-sm-6 col-xs-12">
                    <ActionButton buttonText={"Cancel"} buttonAction={this.props.createNewJob} className={"button btn-defaultSecondary"}/>
                </div>
                <div className="form-group col-lg-4 col-lg-push-4 col-md-4 col-md-push-4 col-sm-6 col-sm-push-0 col-xs-12 col-xs-push-0">
                    <ActionButton buttonText={CREATE_MANUAL_JOB_TITLE} buttonAction={() => this.onClick()} disabled={this.state.loading}/>
                </div>
            </>
        );
    }
}
